import React, { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { usePageViewEvents } from '../../../../core/Analytics/usePageViewEvents';
import { PageView, UserInteractionEvent } from '../../../../models/analytics';
import { Credentials } from '../../../../models/credentials';
import { HandoffCredentialsAction } from '../../../../models/handoffSession';
import { handoffFlowAvailable, ProviderEntity } from '../../../../models/provider';
import { credentialsService as credentialsServiceImport } from '../../../../services/credentialsService';

import { ApplicationErrorPayload, setApplicationError } from '../../../../stores/app-state-store';
import { ERR_USER_CANCELLED } from '../../../../utilities/error';
import { useAnalytics } from '../../../../core/Analytics/AnalyticsProvider';
import {
  InteractionElement,
  parentMessenger,
} from '../../../../core/Analytics/ParentApplicationEventMessenger';
import { LoadingOverlay } from '../../../../core/screens/LoadingOverlay/LoadingOverlay';
import { useConfiguration } from '../../../ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { applicationParameters } from '../../../../utilities/application-parameters';
import { useAppSelector } from '../../../../stores/redux-store';
import {
  navigateUserToInitialTinkLinkAfterThirdPartyRedirect,
  shouldRedirectToReferPage,
  shouldSkipUserConfirmation,
} from '../../../../models/configuration';
import {
  SubmitAuthCredentials,
  SubmitAuthCredentialsOwnProps,
} from '../../../screens/SubmitCredentials/SubmitAuthCredentials';
import { useSelectedProvider } from '../../../App/ProviderContainer/useSelectedProvider';
import { invalidCredentialsStateError } from '../../../../utilities/tink-link';
import { getCredentialsAppUri } from '../../../../utilities/app-uri/app-uri';
import { useProduct } from '../../../ApplicationProviders/ProductProvider/ProductProvider';
import {
  SubmitCredentialsPayload,
  useSubmitCredentials as useSubmitCredentialsImport,
} from '../../../screens/SubmitCredentials/hooks/useSubmitCredentials/useSubmitCredentials';

export type UpdateCredentialsConsentProps = {
  userHintId: string;
  throwError: (error: ApplicationErrorPayload) => void;
  provider: ProviderEntity;
  onSubmitCredentials: SubmitAuthCredentialsOwnProps['onSubmitCredentials'];
  renderProviderFields: boolean;
};

export const UpdateCredentialsConsentPresenter: React.FC<UpdateCredentialsConsentProps> = ({
  userHintId,
  onSubmitCredentials,
  throwError,
  provider,
  renderProviderFields,
}: UpdateCredentialsConsentProps) => {
  const { tinklinkAnalytics } = useAnalytics();

  usePageViewEvents({
    pageView: PageView.UPDATE_TRANSACTIONS_CONSENT_SCREEN,
  });

  return (
    <SubmitAuthCredentials
      userHintId={userHintId}
      provider={provider}
      handoffCredentialsAction={HandoffCredentialsAction.UPDATE_CREDENTIALS_CONSENT}
      onBack={() => {
        tinklinkAnalytics.sendInteractionEvent({
          action: `${PageView.UPDATE_TRANSACTIONS_CONSENT_SCREEN}/${UserInteractionEvent.CLOSE_BUTTON}`,
          view: PageView.UPDATE_TRANSACTIONS_CONSENT_SCREEN,
        });

        parentMessenger.fireInteractionEvent({
          pageView: PageView.UPDATE_TRANSACTIONS_CONSENT_SCREEN,
          element: InteractionElement.CLOSE_BUTTON,
        });

        throwError(ERR_USER_CANCELLED);
      }}
      renderProviderFields={renderProviderFields}
      onSubmitCredentials={onSubmitCredentials}
      refreshableItems={[]}
    />
  );
};

export const UpdateCredentialsConsent = ({
  children = (props: UpdateCredentialsConsentProps) => (
    <UpdateCredentialsConsentPresenter {...props} />
  ),
  credentialsService = credentialsServiceImport,
  useSubmitCredentials = useSubmitCredentialsImport,
}) => {
  const dispatch = useDispatch();
  const { selectedProvider: provider } = useSelectedProvider();
  const { productNames } = useProduct();

  const { data: configurationData } = useConfiguration();
  const { submitCredentials: submitRegularCredentials } = useSubmitCredentials();
  const initialized = useRef(false);

  const { idHint: userHintId = '', user } = useAppSelector((state) => state.user, shallowEqual);
  const credentials = useAppSelector((state) => state.credentials.data, shallowEqual);
  const userCredentials = useAppSelector((state) => state.userCredentials.data, shallowEqual);

  const userId = user?.id;
  const initialCredentials = userCredentials[0];
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const refreshableItems = applicationParameters.get('refreshableItems');

  const throwError = useCallback(
    (error: ApplicationErrorPayload) => {
      dispatch(setApplicationError(error));
    },
    [dispatch]
  );

  const refreshCredentials = useCallback(
    () =>
      dispatch(
        credentialsService.refreshCredentials({
          credentials: initialCredentials,
          authenticate: true,
          appUri: getCredentialsAppUri({
            redirectToReferPage: shouldRedirectToReferPage(configurationData),
            navigateToInitialTinkLinkRedirect:
              navigateUserToInitialTinkLinkAfterThirdPartyRedirect(configurationData),
          }),
          refreshableItems,
          productNames,
        })
      ),
    [
      initialCredentials,
      credentialsService,
      productNames,
      configurationData,
      refreshableItems,
      dispatch,
    ]
  );

  const submitCredentials = useCallback(
    ({ fields }: SubmitCredentialsPayload) =>
      submitRegularCredentials({ request: { fields }, refreshableItems, credentials }),
    [submitRegularCredentials, refreshableItems, credentials]
  );

  useEffect(() => {
    const initialize = async () => {
      initialized.current = true;

      if (Credentials.isAwaitingThirdPartyAppAuthentication(initialCredentials)) {
        dispatch(setApplicationError(invalidCredentialsStateError));

        return;
      }

      const skipUserIdentityConfirmation = shouldSkipUserConfirmation(configurationData);
      const isHandoffFlowAvailable = handoffFlowAvailable(provider, configurationData, userId);

      if (skipUserIdentityConfirmation && !isHandoffFlowAvailable) {
        refreshCredentials();
      }

      setIsLoading(false);
    };

    if (!initialized.current) {
      initialize();
    }
  }, [
    dispatch,
    provider,
    configurationData,
    refreshCredentials,
    initialCredentials,
    productNames,
    userId,
  ]);

  if (isLoading) {
    return <LoadingOverlay />;
  }

  if (credentials) {
    return children({
      renderProviderFields: true,
      onSubmitCredentials: submitCredentials,
      provider,
      userHintId,
      throwError,
    });
  }

  return children({
    renderProviderFields: false,
    onSubmitCredentials: refreshCredentials,
    provider,
    userHintId,
    throwError,
  });
};
